/**
 * Settings → Backup — tenant self-service backup & monthly reminder.
 *
 * Backend endpoints used:
 *   GET  /api/organizations/mine/export              → downloads a full ZIP snapshot
 *   GET  /api/organizations/mine/backup-schedule     → returns current schedule
 *   PUT  /api/organizations/mine/backup-schedule     → saves { enabled, day_of_month, notify_email }
 *
 * The monthly reminder is a nudge email — the actual download still requires
 * the user to log in and click the button here (cookie-auth). Simple, safe.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, ShieldCheck, Loader2, Calendar as CalIcon, Mail } from "lucide-react";
import { downloadBlob } from "@/lib/download";

const DAYS = Array.from({ length: 28 }, (_, i) => i + 1);

export default function BackupTab() {
  const [sched, setSched] = useState({ enabled: false, day_of_month: 1, notify_email: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/organizations/mine/backup-schedule");
        setSched(r.data);
      } catch (e) { /* fallback silent */ }
      setLoading(false);
    })();
  }, []);

  const save = async (patch) => {
    const next = { ...sched, ...patch };
    setSched(next);
    setSaving(true);
    try {
      const r = await api.put("/organizations/mine/backup-schedule", next);
      setSched(r.data.backup_schedule);
      toast.success("Backup schedule saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    setSaving(false);
  };

  const downloadNow = async () => {
    setDownloading(true);
    try {
      await downloadBlob("/organizations/mine/export", `zenith360_backup_${new Date().toISOString().slice(0,10)}.zip`);
      toast.success("Backup ZIP downloaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Download failed"); }
    setDownloading(false);
  };

  if (loading) return <div className="text-sm text-muted-foreground p-4">Loading backup settings…</div>;

  return (
    <div className="space-y-6" data-testid="backup-tab">
      {/* On-demand download */}
      <div className="card-sharp p-5 space-y-3" data-testid="backup-download-card">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Backup · On demand</div>
            <div className="text-lg font-semibold mt-0.5">Download a full ZIP snapshot of your data</div>
            <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Includes every scoped collection — accounts, parties, items, warehouses,
              vouchers, sales invoices, purchase bills, credit/debit notes, recurring
              documents, users (password hashes excluded). Encoded as a single JSON in
              a ZIP archive. Perfect for handing off to your CA or archiving before a
              major migration.
            </div>
          </div>
          <Button onClick={downloadNow} disabled={downloading} className="rounded-sm gap-2 shrink-0" data-testid="backup-download-now">
            {downloading ? <><Loader2 className="w-4 h-4 animate-spin" /> Preparing…</> : <><Download className="w-4 h-4" /> Download ZIP</>}
          </Button>
        </div>
      </div>

      {/* Monthly reminder toggle */}
      <div className="card-sharp p-5 space-y-4" data-testid="backup-schedule-card">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent">// Backup · Monthly reminder</div>
          <div className="text-lg font-semibold mt-0.5">Email me every month to download my backup</div>
          <div className="text-xs text-muted-foreground mt-1 leading-relaxed">
            We'll send a branded reminder email on your chosen day — no attachment, just a
            one-click link back to this page. You still need to log in and hit
            "Download ZIP" above (that's a security feature, not a bug — signed URLs
            that expose company data would be risky in shared inboxes).
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-sm cursor-pointer" htmlFor="backup-enabled">
            <Switch id="backup-enabled" checked={sched.enabled}
              onCheckedChange={(v) => save({ enabled: v })}
              data-testid="backup-enabled" />
            <span className="font-medium">{sched.enabled ? "Reminder enabled" : "Reminder disabled"}</span>
          </label>
          <div className={`flex items-center gap-2 ${sched.enabled ? "" : "opacity-40 pointer-events-none"}`}>
            <CalIcon className="w-4 h-4 text-muted-foreground" />
            <Label className="text-xs">Day of month:</Label>
            <Select value={String(sched.day_of_month)} onValueChange={(v) => save({ day_of_month: parseInt(v, 10) })}>
              <SelectTrigger className="w-24 text-sm rounded-sm" data-testid="backup-day-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {DAYS.map(d => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <span className="text-[10px] text-muted-foreground">(1–28, always exists in every month)</span>
          </div>
        </div>

        <div className={`flex items-end gap-2 ${sched.enabled ? "" : "opacity-40 pointer-events-none"}`}>
          <div className="flex-1 max-w-md">
            <Label className="flex items-center gap-1.5 text-xs"><Mail className="w-3 h-3" /> Notification email</Label>
            <Input value={sched.notify_email || ""} onChange={e => setSched(s => ({ ...s, notify_email: e.target.value }))}
              onBlur={() => save({ notify_email: sched.notify_email })}
              placeholder="admin@company.com" className="mt-1 text-sm" data-testid="backup-notify-email" />
          </div>
          {sched.last_notified_at && (
            <div className="text-[10px] text-muted-foreground pb-2">
              Last reminder sent: {sched.last_notified_at.slice(0, 10)}
            </div>
          )}
        </div>

        {saving && <div className="text-[10px] text-muted-foreground flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</div>}
      </div>

      {/* Info panel */}
      <div className="card-sharp p-5 bg-muted/30" data-testid="backup-dr-info">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-2">// Behind the scenes · 6-tier DR</div>
        <div className="text-xs text-muted-foreground leading-relaxed space-y-1">
          <div>Beyond this self-service ZIP, Zenith 360 runs a 6-tier disaster-recovery stack for you:</div>
          <div>· <b>Tier 1</b> · Soft-delete + Trash restore (30 days) — you handle this yourself</div>
          <div>· <b>Tier 2</b> · Full write-audit stream (every mutation logged with before/after + IP + user)</div>
          <div>· <b>Tier 3</b> · MongoDB Atlas continuous point-in-time (7-day PITR)</div>
          <div>· <b>Tier 4</b> · Cross-region snapshot copy (Mumbai → Hyderabad, nightly)</div>
          <div>· <b>Tier 5</b> · Weekly cold archive to S3 Glacier Deep (AES-256 encrypted, 7-year retention)</div>
          <div>· <b>Tier 6</b> · This tenant self-export you just downloaded</div>
        </div>
      </div>
    </div>
  );
}
